import { SceneKey } from './sceneKeys';
import { GameMode } from '../types/GameMode';
import { HIDDEN_MONSTER_IDS } from '../constants/monsters';
import { GameProgress } from '../types/GameProgress';

/**
 * 隠しキャラクターのキャラ選択画面での表示モード
 * - 'none'     : 表示しない（ボス未撃破）
 * - 'unknown'  : 「？？？」表示（ボス撃破済み・未解放）
 * - 'available': 通常表示（解放済み）
 */
export type HiddenMonsterDisplayMode = 'none' | 'unknown' | 'available';

/**
 * 隠しキャラクターの表示モードを返す。
 * 非隠しキャラに対しては常に 'available' を返す。
 */
export function getHiddenMonsterDisplayMode(
  monsterId: string,
  progress: GameProgress,
): HiddenMonsterDisplayMode {
  if (!HIDDEN_MONSTER_IDS.includes(monsterId)) return 'available';
  if (progress.unlockedHiddenMonsters.includes(monsterId)) return 'available';
  if (progress.bossDefeatedWith.length > 0) return 'unknown';
  return 'none';
}

/** 初期解放キャラのID（衡騎士：レイン） */
export const INITIAL_MONSTER_ID = 'zaag';

/**
 * キャラ解放順序（PROJECT.mdのステージ順と一致）
 * ステージNをクリアすると UNLOCK_ORDER[N-1] が解放される
 */
export const UNLOCK_ORDER: string[] = [
  'gardan', // ステージ1: ドルグ
  'morsu', // ステージ2: ユグド
  'roona', // ステージ3: シエル
  'balga', // ステージ4: ゲンブ
  'zephyr', // ステージ5: カイ
  'graon', // ステージ6: ボルグ
  'igna', // ステージ7: アーシュ
];

/**
 * クリアしたステージ数に基づいて解放済みモンスターIDの一覧を返す
 */
export function getUnlockedMonsterIds(clearedStages: number): string[] {
  const count = Math.max(0, Math.min(clearedStages, UNLOCK_ORDER.length));
  return [INITIAL_MONSTER_ID, ...UNLOCK_ORDER.slice(0, count)];
}

/**
 * 指定モンスターが解放済みかどうかを返す
 */
export function isMonsterUnlocked(monsterId: string, clearedStages: number): boolean {
  return getUnlockedMonsterIds(clearedStages).includes(monsterId);
}

/** グリッド列数 */
export const GRID_COLS = 5;

/** グリッド行数 */
export const GRID_ROWS = 2;

/** 魂格別テーマカラー（パラメータパネル等のテキスト用。暗背景で視認できる明度を確保） */
export const THEME_COLORS: Record<string, string> = {
  zaag: '#c0c0c0',  // 銀/白
  gardan: '#d4a030', // 金土色（元:#8b6914 → 明度UP）
  roona: '#87ceeb',  // 淡青
  zephyr: '#3dc47a', // 翠緑（元:#2e8b57 → 明度UP）
  balga: '#6688ff',  // 青（元:#191970 → 明度UP）
  morsu: '#55cc55',  // 緑（元:#006400 → 明度UP）
  graon: '#cc55cc',  // 紫（元:#800080 → 明度UP）
  igna: '#ff6633',   // 赤橙
  alice: '#cc88ff',  // 淡紫（元:#2d0047 → 明度UP）
  mococo: '#ff99cc', // パステルピンク
};

/** キャラ選択画面のステップ */
export type CharacterSelectStep = 'player' | 'opponent' | 'cpu1' | 'cpu2'
  | 'player2' | 'player3' | 'opponent2' | 'opponent3';

/** キャラ選択画面のヘッダーテキスト */
export const CHARACTER_SELECT_HEADERS = {
  player: 'キャラ選択（自分）',
  opponent: 'キャラ選択（相手）',
  cpu1: 'キャラ選択（CPU1）',
  cpu2: 'キャラ選択（CPU2）',
  player2: 'キャラ選択（自分 2体目）',
  player3: 'キャラ選択（自分 3体目）',
  opponent2: 'キャラ選択（相手 2体目）',
  opponent3: 'キャラ選択（相手 3体目）',
  default: 'キャラ選択',
} as const;

/** キャラ選択画面のボタン設定 */
export interface CharacterSelectButtonConfig {
  /** ボタンのラベル */
  label: string;
  /** 遷移先シーン */
  targetScene: SceneKey;
  /** ボタンの役割 */
  action: 'confirm' | 'back' | 'random';
}

/** キャラ選択画面のデフォルトボタン定義 */
export const CHARACTER_SELECT_BUTTONS: CharacterSelectButtonConfig[] = [
  {
    label: '決定',
    targetScene: SceneKey.BATTLE,
    action: 'confirm',
  },
  {
    label: 'ランダム',
    targetScene: SceneKey.BATTLE,
    action: 'random',
  },
  {
    label: '戻る',
    targetScene: SceneKey.MODE_SELECT,
    action: 'back',
  },
];

/** 詳細パネルのレイアウト設定 */
export const DETAIL_PANEL_CONFIG = {
  /** オーバーレイのdepth */
  overlayDepth: 100,
  /** パネル幅 */
  panelWidth: 660,
  /** パネル高さ */
  panelHeight: 360,
  /** パネル中心X */
  panelCenterX: 480,
  /** パネル中心Y */
  panelCenterY: 270,
  /** 行間 */
  lineHeight: 32,
} as const;

/**
 * 挑戦モードのヘッダーテキストを返す
 */
export function getChallengeHeader(stageNumber: number): string {
  return `挑戦モード - ステージ${stageNumber}`;
}

/**
 * ステップとモードに応じたボタン定義を返す
 */
export function getCharacterSelectButtons(
  step?: CharacterSelectStep,
  mode?: GameMode,
): CharacterSelectButtonConfig[] {
  if (mode === GameMode.SURVIVAL) {
    return [
      { label: '決定', targetScene: SceneKey.BATTLE, action: 'confirm' },
      { label: '戻る', targetScene: SceneKey.TITLE, action: 'back' },
    ];
  }
  if (mode === GameMode.FREE_LOCAL) {
    return [
      { label: '決定', targetScene: SceneKey.BATTLE, action: 'confirm' },
      { label: 'ランダム', targetScene: SceneKey.BATTLE, action: 'random' },
      { label: '戻る', targetScene: SceneKey.BATTLE_MODE_SELECT, action: 'back' },
    ];
  }
  if (mode === GameMode.CHALLENGE) {
    return [
      { label: '決定', targetScene: SceneKey.BATTLE, action: 'confirm' },
      { label: 'ランダム', targetScene: SceneKey.BATTLE, action: 'random' },
      { label: '戻る', targetScene: SceneKey.STAGE_SELECT, action: 'back' },
    ];
  }
  if (mode === GameMode.FREE_CPU) {
    if (step === 'player') {
      return [
        { label: '決定', targetScene: SceneKey.CHARACTER_SELECT, action: 'confirm' },
        { label: 'ランダム', targetScene: SceneKey.CHARACTER_SELECT, action: 'random' },
        { label: '戻る', targetScene: SceneKey.BATTLE_MODE_SELECT, action: 'back' },
      ];
    }
    if (step === 'opponent') {
      return [
        { label: '決定', targetScene: SceneKey.DIFFICULTY_SELECT, action: 'confirm' },
        { label: 'ランダム', targetScene: SceneKey.DIFFICULTY_SELECT, action: 'random' },
        { label: '戻る', targetScene: SceneKey.CHARACTER_SELECT, action: 'back' },
      ];
    }
  }
  if (mode === GameMode.FREE_CPU_3V3) {
    const isLastPlayerStep = step === 'player3';
    const isLastOpponentStep = step === 'opponent3';
    if (step === 'player' || step === 'player2' || step === 'player3') {
      return [
        { label: '決定', targetScene: SceneKey.CHARACTER_SELECT, action: 'confirm' },
        { label: 'ランダム', targetScene: SceneKey.CHARACTER_SELECT, action: 'random' },
        {
          label: '戻る',
          targetScene: isLastPlayerStep ? SceneKey.CHARACTER_SELECT : SceneKey.BATTLE_MODE_SELECT,
          action: 'back',
        },
      ];
    }
    if (step === 'opponent' || step === 'opponent2' || step === 'opponent3') {
      return [
        {
          label: '決定',
          targetScene: isLastOpponentStep ? SceneKey.DIFFICULTY_SELECT : SceneKey.CHARACTER_SELECT,
          action: 'confirm',
        },
        {
          label: 'ランダム',
          targetScene: isLastOpponentStep ? SceneKey.DIFFICULTY_SELECT : SceneKey.CHARACTER_SELECT,
          action: 'random',
        },
        { label: '戻る', targetScene: SceneKey.CHARACTER_SELECT, action: 'back' },
      ];
    }
  }
  if (mode === GameMode.SPECTATOR) {
    if (step === 'cpu1') {
      return [
        { label: '決定', targetScene: SceneKey.CHARACTER_SELECT, action: 'confirm' },
        { label: 'ランダム', targetScene: SceneKey.CHARACTER_SELECT, action: 'random' },
        { label: '戻る', targetScene: SceneKey.BATTLE_MODE_SELECT, action: 'back' },
      ];
    }
    if (step === 'cpu2') {
      return [
        { label: '決定', targetScene: SceneKey.DIFFICULTY_SELECT, action: 'confirm' },
        { label: 'ランダム', targetScene: SceneKey.DIFFICULTY_SELECT, action: 'random' },
        { label: '戻る', targetScene: SceneKey.CHARACTER_SELECT, action: 'back' },
      ];
    }
  }
  return CHARACTER_SELECT_BUTTONS;
}
